import type { MouseEvent } from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EventCard } from '@/components';
import { EngineService } from '@/services/engine';
import { useSourcesStore } from '@/stores';
import { useAddEventToReportMenuItem, useExportEventMenuItem } from '@/hooks';
import type { EventsExportControls } from '@/hooks';
import {
    usePipEventWatcher,
    usePipPlayerStore,
} from '@/stores/pip-player.store';
import { isVideoRangeEvent } from '@/utils/date';
import { getDetectionLabel } from '@/utils/detection-classes';
import type { Event } from '@/types/event';
import { buildEventCardMetadata } from '@/utils/event-card-metadata';
import { getEventTimeRange } from '@/utils/event-time-range';
export interface EventListItem {
    event: Event;
    confidence?: number;
    sourceName?: string;
}

interface EventsListCardsProps {
    items: EventListItem[];
    onEventClick: (eventId: string, event?: MouseEvent) => void;
    selectable: boolean;
    selectedEventIds: Set<string>;
    onSelectToggle: (eventId: string) => void;
    eventExport: EventsExportControls;
}

export function EventsListCards({
    items,
    onEventClick,
    selectable,
    selectedEventIds,
    onSelectToggle,
    eventExport,
}: EventsListCardsProps) {
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
    ]);
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const addSources = useSourcesStore((state) => state.addSources);
    const pipOpen = usePipPlayerStore((s) => s.open);
    const isPipPlaying = usePipEventWatcher();
    const addToReportItem = useAddEventToReportMenuItem();
    const exportEventItem = useExportEventMenuItem();
    const refreshPipNavigationContext = usePipPlayerStore(
        (s) => s.refreshNavigationContext,
    );
    useEffect(() => {
        refreshPipNavigationContext({
            type: 'events-page',
            items: items.map((i) => ({ id: i.event.id })),
        });
    }, [items, refreshPipNavigationContext]);

    const missingSourceIds = useMemo(() => {
        const ids = new Set<string>();
        for (const item of items) {
            const sourceId = item.event.sourceId;
            if (sourceId && !getSourceById(sourceId)) {
                ids.add(sourceId);
            }
        }
        return Array.from(ids);
    }, [items, getSourceById]);

    useEffect(() => {
        if (missingSourceIds.length === 0) return;
        let cancelled = false;

        EngineService.getSourcesByIds(missingSourceIds)
            .then((sources) => {
                if (!cancelled && sources.length > 0) {
                    addSources(sources);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [missingSourceIds, addSources]);

    return (
        <>
            {items.map((item, index) => {
                const event = item.event;
                const source = getSourceById(event.sourceId);
                const sourceType = event.sourceType ?? source?.type;
                const { startTime: eventStartTime, endTime: eventEndTime } =
                    getEventTimeRange(event);
                const isVideoRange = isVideoRangeEvent(
                    eventStartTime,
                    eventEndTime,
                    sourceType,
                );
                const sourceLabel =
                    item.sourceName ??
                    event.sourceName ??
                    source?.displayName ??
                    '';
                const metadata = buildEventCardMetadata({
                    createdAt: event.createdAt,
                    eventStartTime,
                    eventEndTime,
                    isVideoRange,
                    sourceType,
                    sourceLabel,
                    locationName: source?.location?.name,
                });

                const isWatchingThis = isPipPlaying(event.id);

                const title = getDetectionLabel(t, event.classId);

                return (
                    <EventCard
                        key={event.id}
                        id={event.id}
                        title={title}
                        thumbnailUrls={[
                            event.thumbnailUrl ||
                                (event.type === 'SCENE'
                                    ? EngineService.getSceneThumbnailUrl(
                                          event.id,
                                      )
                                    : EngineService.getEventThumbnailUrl(
                                          event.id,
                                          true,
                                      )),
                        ]}
                        confidence={item.confidence}
                        isScene={event.type === 'SCENE'}
                        metadata={metadata}
                        quickView={{
                            state: isWatchingThis ? 'watching' : 'idle',
                            onClick: () =>
                                pipOpen({
                                    eventId: event.id,
                                    navigationContext: {
                                        type: 'events-page',
                                        items: items.map((i) => ({
                                            id: i.event.id,
                                        })),
                                        currentIndex: index,
                                    },
                                }),
                        }}
                        onClick={onEventClick}
                        extraActions={[
                            ...addToReportItem(event, {
                                confidence: item.confidence,
                            }),
                            ...exportEventItem(event, eventExport),
                        ]}
                        selectable={selectable}
                        selected={selectedEventIds.has(event.id)}
                        onSelectToggle={onSelectToggle}
                    />
                );
            })}
        </>
    );
}
